import { __spread, __decorate, __param, __metadata } from 'tslib';
import { ɵɵdefineInjectable, ɵɵinject, RendererFactory2, Injectable, Inject, Optional, PLATFORM_ID, NgModule } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Subject } from 'rxjs';
import { Router, NavigationEnd, NavigationError, NavigationCancel, NavigationStart } from '@angular/router';
import { filter, delay } from 'rxjs/operators';

var UkLazyLoadingService = /** @class */ (function () {
    function UkLazyLoadingService(document, rendererFactory) {
        this.document = document;
        this.loadedScripts = [];
        this.loadingScripts = [];
        this.loadedStyles = [];
        this.loadingStyles = [];
        this.requestedScripts = new Array();
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    /**
     * Attach new styles file to DOM
     * @param url URL to load styles file
     * @returns Observable<boolean>
     */
    UkLazyLoadingService.prototype.loadStyles = function (url) {
        var isLoaded = this.loadedStyles.includes(url);
        var loaded$ = new BehaviorSubject(false);
        if (!this.loadingStyles.includes(url) && isLoaded) {
            this.loadingStyles = __spread(this.loadingStyles, [url]);
            var style = this.renderer.createElement('link');
            this.renderer.setAttribute(style, 'rel', 'stylesheet');
            this.renderer.setAttribute(style, 'href', url);
            this.renderer.appendChild(this.document.head, style);
            return loaded$.asObservable();
        }
        else {
            return loaded$.asObservable();
        }
    };
    /**
     * Attach new script file to DOM
     * @param url         URL to load script file
     * @param async       Flag to load in async way
     * @param attributes  Attributes to set in script
     * @returns Observable<boolean>
     */
    UkLazyLoadingService.prototype.loadScript = function (url, async, attributes) {
        var _this = this;
        if (async === void 0) { async = true; }
        var isLoaded = this.loadedScripts.includes(url);
        var loaded$ = new BehaviorSubject(false);
        var index = this.findUrl(url);
        if (index < 0 && !isLoaded) {
            this.requestedScripts.push({ urlScript: url, observables: [loaded$] });
            this.loadingScripts = __spread(this.loadingScripts, [url]);
            var script_1 = this.renderer.createElement('script');
            this.renderer.setAttribute(script_1, 'type', 'text/javascript');
            this.renderer.setAttribute(script_1, async ? 'async' : 'defer', '');
            this.renderer.setAttribute(script_1, 'src', url);
            if (attributes) {
                attributes.forEach(function (attr) {
                    _this.renderer.setAttribute(script_1, attr.name, attr.value);
                });
            }
            this.renderer.listen(script_1, 'load', function () {
                _this.scriptLoaded(url);
                _this.toTrue(url, true);
            });
            this.renderer.listen(script_1, 'error', function () {
                _this.scriptLoaded(url);
                _this.toTrue(url, false);
            });
            this.renderer.appendChild(this.document.head, script_1);
            return loaded$.asObservable();
        }
        else {
            this.requestedScripts[index]['observables'] = __spread(this.requestedScripts[index]['observables'], [loaded$]);
            return loaded$.asObservable();
        }
    };
    UkLazyLoadingService.prototype.scriptLoaded = function (url) {
        this.loadingScripts = this.loadingScripts.filter(function (script) { return script !== url; });
    };
    UkLazyLoadingService.prototype.findUrl = function (url) {
        var index = this.requestedScripts.findIndex(function (x) { return x['urlScript'] === url; });
        return index;
    };
    UkLazyLoadingService.prototype.toTrue = function (url, pass) {
        var index = this.findUrl(url);
        this.requestedScripts[index].observables.map(function (a) {
            a.next(pass);
            a.complete();
        });
    };
    UkLazyLoadingService.ngInjectableDef = ɵɵdefineInjectable({ factory: function UkLazyLoadingService_Factory() { return new UkLazyLoadingService(ɵɵinject(DOCUMENT), ɵɵinject(RendererFactory2)); }, token: UkLazyLoadingService, providedIn: "root" });
    UkLazyLoadingService = __decorate([
        Injectable({ providedIn: 'root' }),
        __param(0, Inject(DOCUMENT)),
        __metadata("design:paramtypes", [Object, RendererFactory2])
    ], UkLazyLoadingService);
    return UkLazyLoadingService;
}());

var UkUserAgentDetectorService = /** @class */ (function () {
    function UkUserAgentDetectorService(_platformId) {
        this._platformId = _platformId;
        this.hasTouchScreen = false;
        this.ua = navigator.userAgent;
        this.isBrowser = this._platformId ?
            isPlatformBrowser(this._platformId) : typeof document === 'object' && !!document;
        this.TRIDENT = this.isBrowser && /(msie|trident)/i.test(this.ua);
        this.IOS = this.isBrowser && /iPad|iPhone|iPod/.test(this.ua) &&
            !('MSStream' in window);
        this.ANDROID = this.isBrowser && /android/i.test(this.ua) && !this.TRIDENT;
        this.WINDOWS_PHONE = this.isBrowser && /IEMobile|Windows Phone/i.test(this.ua);
        this.BLACKBERRY = this.isBrowser && /BlackBery|BB|PlayBook/i.test(this.ua);
        this.WEBOS = this.isBrowser && /webOS/i.test(this.ua);
        // BROWSER DETECTION
        // Internet Explorer 6-11
        this.isIE =  !!document.documentMode;
        // Edge 20+
        this.isEdge = !this.isIE && !!window.StyleMedia;
    }
    /**
     * Get User Agent
     * @returns string
     */
    UkUserAgentDetectorService.prototype.getUserAgent = function () {
        // Mobile device detection method found here:
        // https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
        if ('maxTouchPoints' in navigator) {
            this.hasTouchScreen = navigator.maxTouchPoints > 0;
        }
        else if ('msMaxTouchPoints' in navigator) {
            this.hasTouchScreen = window.navigator.msMaxTouchPoints > 0;
        }
        else {
            var mQ = window.matchMedia && matchMedia('(pointer:coarse)');
            if (mQ && mQ.media === '(pointer:coarse)') {
                this.hasTouchScreen = !!mQ.matches;
            }
            else if ('orientation' in window) {
                this.hasTouchScreen = true;
            }
            else {
                this.hasTouchScreen = (this.WINDOWS_PHONE || this.BLACKBERRY || this.WEBOS ||
                    this.ANDROID || this.IOS);
            }
        }
        return !this.hasTouchScreen ? 'DESKTOP' :
            this.WINDOWS_PHONE ? 'OTHER_MOBILE' :
                this.ANDROID ? 'ANDROID' :
                    this.IOS ? 'IOS' :
                        'OTHER_MOBILE';
    };
    /**
     * Check if user agent is Internet Explorer
     * @returns boolean
     */
    UkUserAgentDetectorService.prototype.getIsIE = function () {
        return this.isIE;
    };
    /**
     * Check if user agent is Microsoft Edge
     * @returns boolean
     */
    UkUserAgentDetectorService.prototype.getIsEdge = function () {
        return this.isEdge;
    };
    UkUserAgentDetectorService = __decorate([
        Injectable(),
        __param(0, Optional()), __param(0, Inject(PLATFORM_ID)),
        __metadata("design:paramtypes", [Object])
    ], UkUserAgentDetectorService);
    return UkUserAgentDetectorService;
}());

var UkLoadingService = /** @class */ (function () {
    function UkLoadingService(router) {
        var _this = this;
        this.delayRequest$ = new Subject();
        this.loading$ = new BehaviorSubject(true);
        this._requestStack = [];
        this._customTasksCount = 0;
        this._inTransition = false;
        // Listen for transtion end
        router.events.pipe(filter(function (event) { return event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationCancel; })).subscribe(function () { return _this.transition(false); });
        // Listen for transtion start
        router.events.pipe(filter(function (event) { return event instanceof NavigationStart; })).subscribe(function () { return _this.transition(true); });
        this.delayRequest$.pipe(delay(500))
            .subscribe(function (id) { return _this.updateRequest(id, false); });
    }
    Object.defineProperty(UkLoadingService.prototype, "requestsInProgress", {
        get: function () { return this._requestStack.length > 0; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UkLoadingService.prototype, "tasksInProgress", {
        get: function () { return this._customTasksCount > 0; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UkLoadingService.prototype, "inTransition", {
        get: function () { return this._inTransition; },
        enumerable: true,
        configurable: true
    });
    UkLoadingService.prototype.updateRequest = function (id, add) {
        var requestExist = this._requestStack.includes(id);
        if (add && !requestExist) {
            this._requestStack = __spread(this._requestStack, [id]);
        }
        if (!add && requestExist) {
            this._requestStack = __spread(this._requestStack.filter(function (rId) { return rId !== id; }));
        }
        this.onChange();
    };
    UkLoadingService.prototype.updateTask = function (increase) {
        this._customTasksCount = this._customTasksCount + increase;
        this.onChange();
    };
    UkLoadingService.prototype.onChange = function () {
        var _loading = this.requestsInProgress || this.inTransition || this.tasksInProgress;
        if (_loading !== this.loading$.value) {
            this.loading$.next(_loading);
        }
    };
    UkLoadingService.prototype.transition = function (start) {
        this._inTransition = start;
        // Resets any open task after transition
        if (this.inTransition && this.tasksInProgress) {
            this._customTasksCount = 0;
        }
        this.onChange();
    };
    /**
     * Start request saving the UUID of request
     * @param id    UUID of request
     */
    UkLoadingService.prototype.requestStart = function (id) { this.updateRequest(id, true); };
    /**
     * Stop request with certain UUID
     * @param id    UUID of request
     */
    UkLoadingService.prototype.requestStop = function (id) { this.delayRequest$.next(id); };
    /**
     * New task to tasklist
     */
    UkLoadingService.prototype.startTask = function () { this.updateTask(1); };
    /**
     * Pop one task from tasklist
     */
    UkLoadingService.prototype.endTask = function () { this.updateTask(-1); };
    UkLoadingService = __decorate([
        Injectable(),
        __metadata("design:paramtypes", [Router])
    ], UkLoadingService);
    return UkLoadingService;
}());

var UkCoreModule = /** @class */ (function () {
    function UkCoreModule() {
    }
    UkCoreModule = __decorate([
        NgModule({
            providers: [
                UkLazyLoadingService,
                UkUserAgentDetectorService,
                UkLoadingService
            ]
        })
    ], UkCoreModule);
    return UkCoreModule;
}());

export { UkCoreModule, UkLazyLoadingService, UkLoadingService, UkUserAgentDetectorService };
//# sourceMappingURL=uikit-components-uk-core.js.map
